import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getService } from '../services/api';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        setLoading(true);
        const data = await getService(id);
        setService(data);
      } catch (error) {
        console.error('Failed to fetch service:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service not found</h2>
          <button
            onClick={() => navigate('/search')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  const hasAdditionalInfo = 
    (service.specialties && service.specialties.length > 0) ||
    (service.languages && service.languages.length > 0) ||
    (service.age_groups && service.age_groups.length > 0) ||
    (service.target_populations && service.target_populations.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="service-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/search')}
          className="text-blue-600 hover:text-blue-700 mb-6 flex items-center"
          data-testid="back-to-search"
        >
          ← Back to search
        </button>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="border-b pb-6 mb-6">
            <div className="flex justify-between items-start mb-4">
              <h1 className="text-3xl font-bold text-gray-900" data-testid="service-detail-name">
                {service.name}
              </h1>
              <span
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  service.cost === 'free'
                    ? 'bg-green-100 text-green-800'
                    : service.cost === 'low-cost'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
                data-testid="service-detail-cost"
              >
                {service.cost}
              </span>
            </div>
            <p className="text-gray-700 text-lg" data-testid="service-detail-description">
              {service.description}
            </p>
          </div>

          {/* Service Types */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">Service Types</h2>
            <div className="flex flex-wrap gap-2">
              {service.service_types.map((type, index) => (
                <span
                  key={index}
                  className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                  data-testid="service-detail-type"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6 bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="text-gray-500 w-32 flex-shrink-0">Phone:</span>
                <a
                  href={`tel:${service.contact_phone}`}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="service-detail-phone"
                >
                  {service.contact_phone}
                </a>
              </div>
              {service.email && (
                <div className="flex items-start">
                  <span className="text-gray-500 w-32 flex-shrink-0">Email:</span>
                  <a
                    href={`mailto:${service.email}`}
                    className="text-blue-600 hover:text-blue-700"
                    data-testid="service-detail-email"
                  >
                    {service.email}
                  </a>
                </div>
              )}
              {service.website && (
                <div className="flex items-start">
                  <span className="text-gray-500 w-32 flex-shrink-0">Website:</span>
                  <a
                    href={service.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700"
                    data-testid="service-detail-website"
                  >
                    {service.website}
                  </a>
                </div>
              )}
              <div className="flex items-start">
                <span className="text-gray-500 w-32 flex-shrink-0">Location:</span>
                <span data-testid="service-detail-location">
                  {service.city}, {service.state} - {service.pincode}
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-gray-500 w-32 flex-shrink-0">Hours:</span>
                <span data-testid="service-detail-hours">{service.availability_hours}</span>
              </div>
              <div className="flex items-start">
                <span className="text-gray-500 w-32 flex-shrink-0">Mode:</span>
                <div className="flex gap-2">
                  {service.mode.map((m, index) => (
                    <span key={index} className="bg-gray-200 px-2 py-1 rounded text-sm" data-testid="service-detail-mode">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          {hasAdditionalInfo && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-4">Additional Information</h2>
              <div className="space-y-4">
                {service.specialties && service.specialties.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.specialties.map((specialty, index) => (
                        <span key={index} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-sm">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {service.languages && service.languages.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Languages Spoken</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.languages.map((lang, index) => (
                        <span key={index} className="bg-green-50 text-green-700 px-2 py-1 rounded text-sm">
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {service.age_groups && service.age_groups.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Age Groups Served</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.age_groups.map((age, index) => (
                        <span key={index} className="bg-orange-50 text-orange-700 px-2 py-1 rounded text-sm">
                          {age}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {service.target_populations && service.target_populations.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Target Populations</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.target_populations.map((pop, index) => (
                        <span key={index} className="bg-pink-50 text-pink-700 px-2 py-1 rounded text-sm">
                          {pop}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Certifications */}
          {service.certifications && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">Certifications / Registrations</h2>
              <p className="text-gray-700">{service.certifications}</p>
            </div>
          )}

          {/* Treatment Approaches */}
          {service.treatment_approaches && service.treatment_approaches.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-3">Treatment Approaches</h2>
              <div className="flex flex-wrap gap-2">
                {service.treatment_approaches.map((approach, index) => (
                  <span key={index} className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-sm">
                    {approach}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
